// La ficha de un contratado: la columna derecha del chat (13-10, Pau: «al abrir un chat quiero saber quién es, qué
// hemos hecho, por qué está, qué está haciendo, qué planea hacer y qué necesita»; «me gusta la derecha: está siempre
// ahí»). Sustituye a la información de S10 (Pau: «nunca la he usado, mucho texto, denso y difícil»): seis respuestas
// cortas y lo demás plegado en «Más detalles».
// De dónde sale cada cosa (para que nunca mienta):
// - «Qué necesita de ti»: lo pendiente de verdad (situación del contratado: permiso, bloqueo, te escribió…), nunca un
//   resumen. Es lo primero.
// - «Por qué está», «Qué hemos hecho», «Qué hace ahora», «Qué hará después»: la ficha que escribe el resumidor de la
//   oficina al final de cada turno y cada 10 min en turnos largos (backend oficina/ficha.ts), con su antigüedad a la
//   vista; mientras no hay, el último parte del agente.
// Nada técnico: ni ramas, ni ficheros, ni comandos. El nombre se cambia aquí (10-10).
import { ChevronDown, ExternalLink, Pencil } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { formatFechaHora } from '../../../lib/time';
import { useOficinaStore } from '../store';
import { MODELOS } from '../compartido/modelos';
import { LIMITES, POTENCIA_EN_CRISTIANO, type ContratadoDTO, type ExpedienteDTO } from '../compartido/vocabulario';
import { Aviso, Boton, BotonIcono, Campo, IconoEncargo } from '../ui';
import { oficinaApi } from '../api';
import { mensajeError } from '../errores';
import { faseEnCristiano } from '../chat/ConversacionOficina';
import { lineaDelPuesto } from '../plan/puesto';
import { hace, lineaDeEstado, quien } from '../marco/estado';
import { etiquetaSituacion, pasoCierreEnFrase, problemaNecesitaPau } from '../marco/situacion';
import { FilaArchivos, Galeria, useArchivos } from './Galeria';

/** Una ficha sin repasar desde hace más de esto, con el agente trabajando, se marca como vieja. */
const MINUTOS_FICHA_VIEJA = 20;

export type Necesidad = { texto: string; tono: 'pide' | 'nada' | 'problema'; desde: string | null };

/**
 * «Qué necesita de ti», de lo pendiente de verdad (también la línea de «qué pasa ahora»). SI necesita algo lo decide
 * siempre el estado; solo cuando ese estado no dice el qué («espera tu respuesta» a secas), el qué lo pone la ficha
 * si es más nueva que su último mensaje.
 */
export function necesitaDeTi(c: ContratadoDTO, ficha: ExpedienteDTO['ficha'] = null): Necesidad {
  const desde = c.teNecesitaDesde ?? null;
  switch (c.situacion) {
    case 'autorizacion':
    case 'bloqueado':
    case 'te_escribio': {
      if (c.pide.trim()) return { texto: c.pide.trim(), tono: 'pide', desde };
      if (c.visual === 'te_necesita') return { texto: lineaDeEstado(c).texto, tono: 'pide', desde };
      const fresca = ficha?.ahora && ficha.en >= (c.ultimoMensaje?.en ?? '') ? ficha.ahora : '';
      return { texto: fresca || etiquetaSituacion(c.situacion), tono: 'pide', desde };
    }
    case 'problema':
      return problemaNecesitaPau(c) ? { texto: c.pide, tono: 'problema', desde } : { texto: t('oficinaV2.info.ficha.problema'), tono: 'nada', desde: null };
    case 'cerrando':
      return { texto: t('oficinaV2.info.ficha.cerrando'), tono: 'nada', desde: null };
    default:
      return { texto: t('oficinaV2.info.ficha.noNecesita'), tono: 'nada', desde: null };
  }
}

export type Ahora = { tono: 'pide' | 'problema' | 'trabajando' | 'parado' | 'oficina'; etiqueta: string; texto: string };

/**
 * 13-10 (Pau: «me dice "sin nada pendiente" pero no veo qué está haciendo; quiero que el chat me diga si está haciendo
 * algo o si necesita algo de mí»): la línea fija encima del compositor. Lo que necesita sale de lo pendiente de verdad;
 * si trabaja o está parado, del estado real del agente; el detalle, de la ficha si es más nueva que lo último que pasó.
 */
export function ahoraMismo(c: ContratadoDTO, ficha: ExpedienteDTO['ficha']): Ahora {
  const n = necesitaDeTi(c, ficha);
  if (n.tono === 'pide') return { tono: 'pide', etiqueta: t('oficinaV2.chat.ahora.teNecesita'), texto: n.texto };
  if (n.tono === 'problema') return { tono: 'problema', etiqueta: t('oficinaV2.chat.ahora.problema'), texto: n.texto };
  if (c.situacion === 'problema') return { tono: 'problema', etiqueta: t('oficinaV2.chat.ahora.problema'), texto: lineaDeEstado(c).texto };
  if (c.situacion === 'cerrando') return { tono: 'oficina', etiqueta: t('oficinaV2.chat.ahora.cerrando'), texto: '' };
  if (c.situacion === 'llegando') return { tono: 'trabajando', etiqueta: t('oficinaV2.chat.ahora.empezando'), texto: '' };
  const ultimo = [c.ultimoMensaje?.en, c.ultimoInformeEn].filter((x): x is string => !!x).sort().at(-1) ?? '';
  const fresca = ficha?.ahora && ficha.en >= ultimo ? ficha.ahora : '';
  if (c.situacion === 'trabajando') return { tono: 'trabajando', etiqueta: t('oficinaV2.chat.ahora.trabajando'), texto: fresca || c.titular || ficha?.ahora || '' };
  return { tono: 'parado', etiqueta: t('oficinaV2.chat.ahora.parado'), texto: fresca || t('oficinaV2.chat.ahora.paradoSinMas') };
}

export function Informacion({ contratado: c, sala }: { contratado: ContratadoDTO; sala: string }) {
  const exp = useOficinaStore((s) => s.expedientes[c.agentId]);
  const cargar = useOficinaStore((s) => s.cargarExpediente);
  useEffect(() => {
    void cargar(c.agentId);
  }, [cargar, c.agentId]);
  // 13-10 — galería: se recarga cuando hay mensajes nuevos (de él o tuyos).
  const archivos = useArchivos(c.agentId, `${c.ultimoMensaje?.en ?? ''}|${exp?.ficha?.en ?? ''}`);
  const [galeria, setGaleria] = useState(false);
  useEffect(() => {
    setGaleria(false);
  }, [c.agentId]);

  if (galeria && archivos) {
    return (
      <aside className="h-full bg-oficina-panel" aria-label={t('oficinaV2.galeria.titulo')}>
        <Galeria agentId={c.agentId} archivos={archivos} nombreAgente={quien(c)} alVolver={() => setGaleria(false)} />
      </aside>
    );
  }

  return (
    <aside className="flex h-full flex-col overflow-y-auto bg-oficina-panel" aria-label={t('oficinaV2.info.titulo')}>
      {/* Quién es: icono, nombre (se puede cambiar) y su oficio. */}
      <div className="flex items-center gap-3 px-5 pb-4 pt-5">
        <IconoEncargo icono={c.icono} semilla={c.semillaAvatar} tamano={48} />
        <div className="min-w-0 flex-1">
          <Nombre contratado={c} />
          <p className="text-o-sub text-oficina-texto2">{lineaDelPuesto(c.puesto, c.tipoTrabajo)}</p>
        </div>
      </div>

      <Necesita c={c} ficha={exp?.ficha ?? null} />

      {!exp ? (
        <p className="px-5 pt-4 text-o-texto text-oficina-texto3">{t('oficinaV2.chat.cargando')}</p>
      ) : (
        <Ficha c={c} exp={exp} sala={sala} archivos={<FilaArchivos agentId={c.agentId} archivos={archivos} alVerTodo={() => setGaleria(true)} />} />
      )}
    </aside>
  );
}

function Necesita({ c, ficha }: { c: ContratadoDTO; ficha: ExpedienteDTO['ficha'] }) {
  const n = necesitaDeTi(c, ficha);
  return (
    <section className="px-4" aria-label={t('oficinaV2.info.ficha.necesita')}>
      <div
        data-testid="ficha-necesita"
        data-tono={n.tono}
        className={cn(
          'rounded-o-m border px-3.5 py-3',
          n.tono === 'pide' && 'border-oficina-te-necesita/40 bg-oficina-te-necesita/10',
          n.tono === 'problema' && 'border-oficina-situacion-problema-pau/40 bg-oficina-situacion-problema-pau/10',
          n.tono === 'nada' && 'border-oficina-trabajando/25 bg-oficina-trabajando/[0.07]',
        )}
      >
        <h3 className="text-o-nota font-semibold text-oficina-texto3">{t('oficinaV2.info.ficha.necesita')}</h3>
        <p
          className={cn(
            'mt-1 text-o-cuerpo font-medium leading-snug',
            n.tono === 'pide' ? 'text-oficina-te-necesita-claro' : n.tono === 'problema' ? 'text-oficina-error-claro' : 'text-oficina-texto',
          )}
        >
          {n.texto}
        </p>
        {n.desde ? <p className="mt-0.5 text-o-nota text-oficina-texto3">{t('oficinaV2.info.ficha.desde', { hace: hace(n.desde) })}</p> : null}
      </div>
    </section>
  );
}

function Ficha({ c, exp, sala, archivos }: { c: ContratadoDTO; exp: ExpedienteDTO; sala: string; archivos?: ReactNode }) {
  const f = exp.ficha;
  // Con la ficha abierta, «hace 3 min» y «sin actualizar» se recalculan solos cada minuto.
  const [, setTic] = useState(0);
  useEffect(() => {
    const reloj = setInterval(() => setTic((n) => n + 1), 60_000);
    return () => clearInterval(reloj);
  }, []);
  const porQue = f?.porQue || c.resumenParaPau || exp.mision;
  // El cierre en curso manda sobre lo demás: es lo que de verdad está pasando.
  const cierre = exp.cierre.paso
    ? exp.cierre.modo === 'integrar'
      ? t('oficinaV2.cierre.integrando', { paso: minuscula(pasoCierreEnFrase(exp.cierre.paso, 'integrar')) })
      : t(`oficinaV2.cierre.paso.${exp.cierre.paso}`)
    : '';
  const ahora = cierre || f?.ahora || c.titular || faseEnCristiano(c.fase);
  const despues = f?.despues || exp.siguientePaso;
  const vieja = !!f && c.visual === 'trabajando' && Date.now() - Date.parse(f.en) > MINUTOS_FICHA_VIEJA * 60_000;
  return (
    <div className="space-y-4 px-5 pb-6 pt-4">
      <Pregunta titulo={t('oficinaV2.info.ficha.porQue')}>
        <p className="line-clamp-4">{porQue}</p>
      </Pregunta>
      <Pregunta titulo={t('oficinaV2.info.ficha.hecho')}>
        {f?.hecho.length ? (
          <ul className="space-y-1">
            {f.hecho.map((h, i) => (
              <li key={i} className="flex gap-2">
                {/* Un punto, no un ✓: es lo que el agente cuenta, resumido; no está comprobado por la oficina. */}
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-oficina-trabajando" aria-hidden />
                <span className="min-w-0">{h}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-oficina-texto3">{t('oficinaV2.info.ficha.nadaAun')}</p>
        )}
      </Pregunta>
      <Pregunta titulo={t('oficinaV2.info.ficha.ahora')}>
        <p className={cn(cierre && 'text-oficina-cerrando')}>{ahora}</p>
      </Pregunta>
      {despues ? (
        <Pregunta titulo={t('oficinaV2.info.ficha.despues')}>
          <p>{despues}</p>
        </Pregunta>
      ) : null}
      <p data-testid="ficha-antiguedad" className={cn('text-o-nota', vieja ? 'text-oficina-bloqueado' : 'text-oficina-texto3')}>
        {f ? t(vieja ? 'oficinaV2.info.ficha.vieja' : 'oficinaV2.info.ficha.actualizada', { hace: hace(f.en) }) : t('oficinaV2.info.ficha.sinFicha')}
      </p>
      {archivos}
      <MasDetalles c={c} exp={exp} sala={sala} />
    </div>
  );
}

function Pregunta({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-1 text-o-nota font-semibold text-oficina-texto3">{titulo}</h3>
      <div className="text-o-texto leading-relaxed text-oficina-texto">{children}</div>
    </section>
  );
}

/** Lo que casi nunca hace falta, plegado: el encargo tal cual, el informe de cierre y los datos del agente. */
function MasDetalles({ c, exp, sala }: { c: ContratadoDTO; exp: ExpedienteDTO; sala: string }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <section className="border-t border-oficina-borde pt-2">
      <button
        type="button"
        onClick={() => setAbierto(!abierto)}
        aria-expanded={abierto}
        className="flex w-full items-center justify-between rounded-md py-1.5 text-o-sub font-medium text-oficina-texto2 hover:text-oficina-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-oficina-acento"
      >
        {t('oficinaV2.info.ficha.masDetalles')}
        <ChevronDown className={cn('h-4 w-4 transition-transform', abierto && 'rotate-180')} aria-hidden />
      </button>
      {abierto ? (
        <div className="space-y-4 pt-2">
          <Pregunta titulo={t('oficinaV2.info.ficha.loQuePediste')}>
            <MasTexto texto={exp.mision} />
          </Pregunta>
          {exp.cierre.informe ? <InformeFinal exp={exp} /> : null}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-o-texto">
            <dt className="text-oficina-texto3">{t('oficinaV2.info.sala')}</dt>
            <dd className="text-oficina-texto">{sala}</dd>
            <dt className="text-oficina-texto3">{t('oficinaV2.info.modelo')}</dt>
            <dd className="text-oficina-texto">
              {MODELOS.find((m) => m.id === c.modelo)?.nombre ?? t('oficinaV2.desconocido')} · {POTENCIA_EN_CRISTIANO[c.potencia]}
            </dd>
            <dt className="text-oficina-texto3">{t('oficinaV2.info.desde')}</dt>
            <dd className="text-oficina-texto">{formatFechaHora(c.creadoEn)}</dd>
          </dl>
        </div>
      ) : null}
    </section>
  );
}

function Dato({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <p className="mt-1.5 text-o-texto text-oficina-texto2">
      <span className="text-oficina-texto3">{titulo} </span>
      {children}
    </p>
  );
}

function InformeFinal({ exp }: { exp: ExpedienteDTO }) {
  const { informe, integracion, enlace } = exp.cierre;
  if (!informe) return null;
  return (
    // WS15-D8 — la nota de entrega (integró una parte y sigue) no se llama como el informe de cierre (se fue).
    <Pregunta titulo={t(informe.modo === 'integrar' ? 'oficinaV2.info.notaEntrega' : 'oficinaV2.info.informe')}>
      <p className="text-o-cuerpo text-oficina-texto">{informe.resultado}</p>
      {integracion === 'integrada' ? <p className="mt-1 text-o-texto text-oficina-trabajando">{t('oficinaV2.info.integrado')}</p> : null}
      {integracion === 'sin_integrar' ? <p className="mt-1 text-o-texto text-oficina-texto2">{t('oficinaV2.info.sinIntegrar')}</p> : null}
      {informe.noHecho.map((n, i) => (
        <Dato key={i} titulo={t('oficinaV2.info.noHecho')}>
          {t('oficinaV2.info.noHechoLinea', { que: n.que, por_que: n.por_que })}
        </Dato>
      ))}
      {informe.siContinuas ? <Dato titulo={t('oficinaV2.info.siContinuas')}>{informe.siContinuas}</Dato> : null}
      {informe.gasto ? (
        <Dato titulo={t('oficinaV2.info.gasto')}>
          {informe.gasto.enQue
            ? t('oficinaV2.info.gastoLinea', { dolares: informe.gasto.dolares.toFixed(2), en_que: informe.gasto.enQue })
            : t('oficinaV2.info.gastoSolo', { dolares: informe.gasto.dolares.toFixed(2) })}
        </Dato>
      ) : null}
      {enlace ? (
        <a href={enlace} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-o-texto text-oficina-enlace hover:underline">
          {t('oficinaV2.info.verPropuesta')}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      ) : null}
    </Pregunta>
  );
}

function MasTexto({ texto }: { texto: string }) {
  const [abierto, setAbierto] = useState(false);
  const largo = texto.length > 240;
  return (
    <div>
      <p className="whitespace-pre-wrap break-words text-o-texto leading-relaxed text-oficina-texto">{largo && !abierto ? `${texto.slice(0, 240).trimEnd()}…` : texto}</p>
      {largo ? (
        <button type="button" className="mt-1 text-o-sub text-oficina-enlace hover:underline" onClick={() => setAbierto(!abierto)}>
          {abierto ? t('oficinaV2.info.verMenos') : t('oficinaV2.info.verMas')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * El nombre, con un lápiz para cambiarlo (también se llega desde «Cambiar el nombre» del menú ⋮ del chat:
 * `renombrarPedido` del store). Enter guarda, Escape deja el que había; al salir, el foco vuelve al lápiz.
 */
function Nombre({ contratado: c }: { contratado: ContratadoDTO }) {
  const actualizar = useOficinaStore((s) => s.actualizarContratado);
  const pedido = useOficinaStore((s) => s.renombrarPedido === c.agentId);
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lapiz = useRef<HTMLButtonElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  // Solo al cerrar con teclado físico vuelve el foco al lápiz: con el dedo (también la tecla «OK» del teclado
  // del iPhone, que llega como Enter) darle el foco abriría su globo de ayuda encima de la ficha y se quedaría.
  const conTeclado = useRef(false);
  const anotar = (teclado: boolean) => {
    conTeclado.current = teclado && !window.matchMedia('(pointer: coarse)').matches;
  };
  // Si cambias de agente con la ficha abierta, no se queda a medias con el nombre del anterior.
  useEffect(() => {
    setEditando(false);
  }, [c.agentId]);
  useEffect(() => {
    if (!pedido) return;
    useOficinaStore.getState().pedirRenombrar(null);
    abrir();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al llegar el pedido
  }, [pedido]);
  useEffect(() => {
    if (editando) {
      campo.current?.focus();
      campo.current?.select();
    } else if (conTeclado.current) {
      conTeclado.current = false;
      lapiz.current?.focus();
    }
  }, [editando]);

  const limpio = valor.trim();
  const valido = limpio.length >= 3 && limpio.length <= LIMITES.tema;

  function abrir() {
    conTeclado.current = false;
    setValor(c.tema);
    setError(null);
    setEditando(true);
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    if (!valido || enviando) return;
    if (limpio === c.tema) return setEditando(false);
    setEnviando(true);
    setError(null);
    try {
      const r = await oficinaApi.renombrar(c.agentId, limpio);
      // El de ahora (el socket pudo traer otro estado mientras se escribía), no la copia de cuando se abrió.
      const ahora = useOficinaStore.getState().contratados[c.agentId];
      if (ahora) actualizar({ ...ahora, tema: r.tema });
      setEditando(false);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  }

  if (!editando) {
    return (
      <div className="flex max-w-full items-center gap-0.5">
        <h2 className="min-w-0 break-words text-o-cuerpo font-semibold leading-snug text-oficina-texto">{quien(c)}</h2>
        <BotonIcono ref={lapiz} icono={Pencil} etiqueta={t('oficinaV2.info.renombrar')} tamano="sm" onClick={abrir} />
      </div>
    );
  }
  return (
    <form className="w-full space-y-2" onSubmit={(e) => void guardar(e)}>
      <Campo
        ref={campo}
        value={valor}
        maxLength={LIMITES.tema}
        aria-label={t('oficinaV2.info.nombre')}
        aria-describedby={valido ? undefined : `pista-nombre-${c.agentId}`}
        enterKeyHint="done"
        autoComplete="off"
        autoCapitalize="sentences"
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') anotar(true);
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setEditando(false);
          }
        }}
      />
      {valido ? null : (
        <p id={`pista-nombre-${c.agentId}`} className="text-o-sub text-oficina-texto3">
          {t('oficinaV2.info.nombreLargo', { min: 3, max: LIMITES.tema })}
        </p>
      )}
      {error ? <Aviso tono="error">{error}</Aviso> : null}
      <div className="flex gap-2">
        <Boton
          tamano="sm"
          onClick={(e) => {
            anotar(e.detail === 0); // 0 = pulsado con Intro/espacio, no con el dedo ni el ratón
            setEditando(false);
          }}
        >
          {t('oficinaV2.accion.cancelar')}
        </Boton>
        <Boton tamano="sm" variante="primario" type="submit" disabled={!valido} cargando={enviando} onClick={(e) => anotar(e.detail === 0)}>{t('oficinaV2.info.guardarNombre')}</Boton>
      </div>
    </form>
  );
}

/** «Otro agente revisa…» → «otro agente revisa…» (va detrás de «Integrando su trabajo, sin irse:»). */
function minuscula(x: string): string {
  return x.charAt(0).toLowerCase() + x.slice(1);
}
